/**
 * `/oauth-clients` — admin view of registered MCP OAuth clients
 * (OCP-238). Operator-internal: lists every client that completed
 * Dynamic Client Registration or was pre-seeded, with active-
 * authorization counts, per-operator drill-down, delete (dynamic
 * only), and a prune-unused housekeeping action.
 *
 * Data: GET /v1/admin/oauth-clients, GET .../:id/authorizations,
 * DELETE .../:id, POST .../prune-unused.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { useTransientState } from '../lib/useTransientState';
import { fmtDateTime, fmtDate } from '../lib/format-time';

interface ClientRow {
  id: string;
  clientId: string;
  clientName: string;
  isDynamic: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  activeAuthorizationsCount: number;
}

interface Authorization {
  userId: string;
  email: string;
  issuedAt: string;
  expiresAt: string;
  /** OCP-783 - `mcp:read` in the scope = read-only grant. */
  scope: string;
}

export function AdminOAuthClientsPage() {
  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useTransientState<string | null>(null, 4000);
  const [pruning, setPruning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ClientRow | null>(null);
  const [authsFor, setAuthsFor] = useState<ClientRow | null>(null);
  const [auths, setAuths] = useState<Authorization[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api<{ items: ClientRow[] }>('/v1/admin/oauth-clients');
      setClients(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load clients (HTTP ${err.status}).` : 'Could not load clients.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const flash = useCallback((msg: string) => {
    setActionMsg(msg);
  }, []);

  const handleDelete = useCallback(
    async (client: ClientRow) => {
      try {
        await api(`/v1/admin/oauth-clients/${client.id}`, { method: 'DELETE' });
        flash(`Deleted ${client.clientName} — its tokens were revoked.`);
        setConfirmDelete(null);
        await load();
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? (err.body as { message?: string } | null)?.message ?? `HTTP ${err.status}`
            : 'unknown';
        setError(`Could not delete client (${msg}).`);
        setConfirmDelete(null);
      }
    },
    [load, flash],
  );

  const handlePrune = useCallback(async () => {
    setPruning(true);
    try {
      const res = await api<{ deleted_count: number }>('/v1/admin/oauth-clients/prune-unused', { method: 'POST' });
      flash(`Pruned ${res.deleted_count} unused dynamic client${res.deleted_count === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Prune failed (HTTP ${err.status}).` : 'Prune failed.');
    } finally {
      setPruning(false);
    }
  }, [load, flash]);

  const openAuths = useCallback(async (client: ClientRow) => {
    setAuthsFor(client);
    setAuths(null);
    try {
      const res = await api<{ items: Authorization[] }>(`/v1/admin/oauth-clients/${client.id}/authorizations`);
      setAuths(res.items);
    } catch {
      setAuths([]);
    }
  }, []);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">OAuth clients</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            AI clients (Claude Desktop, Cursor, VS Code) authorized to call the OCP MCP server.
          </p>
        </div>
        <button
          type="button"
          onClick={handlePrune}
          disabled={pruning || !clients}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          title="Delete dynamic clients with no active authorizations and no use in 30 days"
        >
          {pruning ? 'Pruning…' : 'Prune unused'}
        </button>
      </div>

      {actionMsg && (
        <div className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
          {actionMsg}
        </div>
      )}
      {error && (
        <div className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!clients ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : clients.length === 0 ? (
        <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-4 py-8 text-center text-sm text-gray-600 dark:text-gray-300">
          No OAuth clients yet. Add <code>https://control.orboto.io/mcp</code> as a custom connector in
          Claude Desktop / Cursor / VS Code and authorize it — it will appear here.
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2 text-right">Active auths</th>
                <th className="px-3 py-2">Registered</th>
                <th className="px-3 py-2">Last used</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {clients.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{c.clientName}</div>
                    <div className="font-mono text-xs text-gray-500">{c.clientId}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                        c.isDynamic
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {c.isDynamic ? 'dynamic' : 'pre-seeded'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {c.activeAuthorizationsCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => void openAuths(c)}
                        className="text-brand-600 hover:underline"
                      >
                        {c.activeAuthorizationsCount}
                      </button>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDateTime(c.createdAt)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {c.lastUsedAt ? fmtDateTime(c.lastUsedAt) : 'never'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(c)}
                      disabled={!c.isDynamic}
                      title={c.isDynamic ? 'Delete client + revoke its tokens' : 'Pre-seeded clients are removed via config, not here'}
                      className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Delete {confirmDelete.clientName}?</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            This revokes every operator&apos;s access + refresh tokens for this client. Any connected AI
            client will get a 401 on its next call and must re-authorize.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleDelete(confirmDelete)}
              className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
            >
              Delete + revoke
            </button>
          </div>
        </Modal>
      )}

      {/* Authorizations drill-down */}
      {authsFor && (
        <Modal onClose={() => setAuthsFor(null)}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Operators authorized for {authsFor.clientName}
          </h2>
          {!auths ? (
            <p className="mt-3 text-sm text-gray-500">Loading…</p>
          ) : auths.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No active authorizations.</p>
          ) : (
            <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-800 text-sm">
              {auths.map((a) => (
                <li key={`${a.userId}-${a.issuedAt}`} className="flex items-center justify-between py-2">
                  <span className="text-gray-900 dark:text-gray-100">
                    {a.email}{' '}
                    <span
                      className={`ml-1 rounded px-1.5 py-0.5 text-[11px] ${a.scope.split(' ').includes('mcp:read') ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'}`}
                      data-testid="grant-access"
                    >
                      {a.scope.split(' ').includes('mcp:read') ? 'read only' : 'read and write'}
                    </span>
                  </span>
                  <span className="text-xs text-gray-500">
                    issued {fmtDate(a.issuedAt)} · expires{' '}
                    {fmtDate(a.expiresAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-gray-500">
            To revoke a single operator&apos;s access without deleting the client, delete + re-add the
            client, or have the operator re-authorize. Per-operator revoke is a follow-up.
          </p>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
}

export default AdminOAuthClientsPage;
